const BASE_URL = 'https://tight-bonus-e005.maxkhmlyt.workers.dev'

export type FormEndpoint = 'home' | 'companies' | 'seafarers' | 'vacancy'

export async function submitForm(
  endpoint: FormEndpoint,
  formData: FormData,
  language: string,
): Promise<{ ok: boolean; errorDetail?: string }> {
  // Add meta fields
  // Always use production domain for pageUrl to avoid Magic Patterns URLs in notifications
  const productionDomain = 'https://elvora.consulting'
  const currentPath = window.location.pathname
  const pageUrl = productionDomain + currentPath

  formData.append('pageUrl', pageUrl)
  formData.append('language', language)

  // Don't send Magic Patterns referer - only send real external referers
  const referer = document.referrer || ''
  if (referer && !referer.includes('magicpatterns.com')) {
    formData.append('referer', referer)
  }

  const url = `${BASE_URL}/webhook/${endpoint}`

  console.log(`[ELVORA Form] Submitting to: ${url}`)
  console.log(
    `[ELVORA Form] Fields:`,
    [...formData.entries()].map(
      ([k, v]) =>
        `${k}=${v instanceof File ? `[File: ${v.name}, ${v.size}b]` : v}`,
    ),
  )

  // ── Attempt 1: Primary fetch with FormData ──
  try {
    const result = await attemptFetch(url, formData)
    console.log(
      `[ELVORA Form] Primary response: status=${result.status}, ok=${result.ok}, body=${result.body}`,
    )
    if (result.ok) return { ok: true }
    console.warn(
      `[ELVORA Form] Primary endpoint "${endpoint}" failed: ${result.status} — ${result.body}`,
    )
  } catch (err: any) {
    console.error(`[ELVORA Form] Primary fetch threw:`, err?.message || err)
  }

  // ── Attempt 2: Try JSON format (no file) ──
  const hasFile = [...formData.entries()].some(([, v]) => v instanceof File)
  if (!hasFile) {
    try {
      console.log(`[ELVORA Form] Trying JSON fallback...`)
      const jsonBody: Record<string, string> = {}
      formData.forEach((value, key) => {
        if (typeof value === 'string') jsonBody[key] = value
      })
      const jsonResult = await attemptJsonFetch(url, jsonBody)
      console.log(
        `[ELVORA Form] JSON response: status=${jsonResult.status}, ok=${jsonResult.ok}, body=${jsonResult.body}`,
      )
      if (jsonResult.ok) return { ok: true }
    } catch (jsonErr: any) {
      console.error(
        `[ELVORA Form] JSON fallback threw:`,
        jsonErr?.message || jsonErr,
      )
    }
  }

  // ── Attempt 3: Fallback to 'home' endpoint ──
  if (endpoint !== 'home') {
    const fallbackUrl = `${BASE_URL}/webhook/home`
    try {
      console.warn(
        `[ELVORA Form] Trying fallback to "home" endpoint: ${fallbackUrl}`,
      )
      const fallbackResult = await attemptFetch(fallbackUrl, formData)
      console.log(
        `[ELVORA Form] Fallback response: status=${fallbackResult.status}, ok=${fallbackResult.ok}, body=${fallbackResult.body}`,
      )
      if (fallbackResult.ok) return { ok: true }
      console.warn(
        `[ELVORA Form] Fallback also failed: ${fallbackResult.status} — ${fallbackResult.body}`,
      )
    } catch (fallbackErr: any) {
      console.error(
        `[ELVORA Form] Fallback fetch threw:`,
        fallbackErr?.message || fallbackErr,
      )
    }
  }

  // ── Attempt 4: XMLHttpRequest (bypasses some fetch restrictions) ──
  try {
    console.warn(`[ELVORA Form] Trying XMLHttpRequest fallback...`)
    const xhrResult = await attemptXHR(url, formData)
    console.log(
      `[ELVORA Form] XHR result: ok=${xhrResult.ok}, status=${xhrResult.status}, body=${xhrResult.body}`,
    )
    if (xhrResult.ok) return { ok: true }
  } catch (xhrErr: any) {
    console.error(
      `[ELVORA Form] XHR fallback also failed:`,
      xhrErr?.message || xhrErr,
    )
  }

  // ── Attempt 5: Simple no-cors beacon (fire-and-forget, last resort) ──
  try {
    console.warn(`[ELVORA Form] Trying navigator.sendBeacon as last resort...`)
    const sent = navigator.sendBeacon(url, formData)
    console.log(`[ELVORA Form] sendBeacon result: ${sent}`)
    // sendBeacon doesn't give us a response, but if it returns true the browser queued it
    if (sent) return { ok: true }
  } catch (beaconErr: any) {
    console.error(
      `[ELVORA Form] sendBeacon failed:`,
      beaconErr?.message || beaconErr,
    )
  }

  console.error(
    `[ELVORA Form] ❌ ALL submission attempts failed for endpoint "${endpoint}"`,
  )
  console.error(
    `[ELVORA Form] 🔍 DIAGNOSIS: Check your Cloudflare Worker at ${BASE_URL}`,
  )
  console.error(`[ELVORA Form] Common issues:`)
  console.error(`  1. Worker is down or not deployed`)
  console.error(`  2. CORS headers missing (needs Access-Control-Allow-Origin)`)
  console.error(`  3. Worker routes /webhook/* not configured`)
  console.error(`  4. Cloudflare free tier limits exceeded`)
  console.error(`  5. Worker code has errors (check Cloudflare dashboard logs)`)

  return {
    ok: false,
    errorDetail:
      'All submission methods failed. Check browser console (F12) for details.',
  }
}

function attemptFetch(
  url: string,
  formData: FormData,
): Promise<{ ok: boolean; status: number; body: string }> {
  return new Promise((resolve, reject) => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => {
      console.warn(`[ELVORA Form] Fetch timeout after 15s for ${url}`)
      controller.abort()
    }, 15000)

    fetch(url, {
      method: 'POST',
      body: formData,
      signal: controller.signal,
    })
      .then(async (response) => {
        clearTimeout(timeoutId)
        let body = ''
        try {
          body = await response.text()
        } catch {
          body = '(could not read response body)'
        }
        resolve({ ok: response.ok, status: response.status, body })
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        reject(error)
      })
  })
}

function attemptJsonFetch(
  url: string,
  data: Record<string, string>,
): Promise<{ ok: boolean; status: number; body: string }> {
  return new Promise((resolve, reject) => {
    const controller = new AbortController()
    const timeoutId = setTimeout(() => controller.abort(), 15000)

    fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      signal: controller.signal,
    })
      .then(async (response) => {
        clearTimeout(timeoutId)
        let body = ''
        try {
          body = await response.text()
        } catch {
          body = '(could not read response body)'
        }
        resolve({ ok: response.ok, status: response.status, body })
      })
      .catch((error) => {
        clearTimeout(timeoutId)
        reject(error)
      })
  })
}

function attemptXHR(
  url: string,
  formData: FormData,
): Promise<{ ok: boolean; status: number; body: string }> {
  return new Promise((resolve, reject) => {
    try {
      const xhr = new XMLHttpRequest()
      xhr.open('POST', url, true)
      xhr.timeout = 15000

      xhr.onload = () => {
        const ok = xhr.status >= 200 && xhr.status < 300
        console.log(
          `[ELVORA Form] XHR onload: status=${xhr.status}, response=${xhr.responseText?.slice(0, 200)}`,
        )
        resolve({ ok, status: xhr.status, body: xhr.responseText || '' })
      }

      xhr.onerror = () => {
        console.error(
          `[ELVORA Form] XHR onerror event — likely CORS or network issue`,
        )
        reject(new Error('XHR network error (likely CORS blocked)'))
      }

      xhr.ontimeout = () => {
        reject(new Error('XHR timeout after 15s'))
      }

      xhr.send(formData)
    } catch (e) {
      reject(e)
    }
  })
}
